import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  Popover,
  Button,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CalendarToday as CalendarIcon,
  Close as CloseIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { apiCall } from '../../services/ApiCall';
import config from '../../config/config.json';

// Timeline Item Component
const TimelineItem = ({ event, isFirst, isLast }) => {
  // Map icon names from API to Material UI icons
  const getIconComponent = (iconName) => {
    const iconMap = {
      email: EmailIcon,
      check_circle: CheckCircleIcon,
      user_plus: PersonAddIcon,
      refresh: RefreshIcon,
      warning: WarningIcon,
      info: InfoIcon,
      hourglass: HourglassEmptyIcon
    };
    return iconMap[iconName] || InfoIcon;
  };

  // Map color names from API to hex colors
  const getColorConfig = (colorName) => {
    const colorMap = {
      blue: { color: '#1976d2', bgcolor: '#e3f2fd' },
      green: { color: '#4caf50', bgcolor: '#e8f5e9' },
      orange: { color: '#ff9800', bgcolor: '#fff3e0' },
      red: { color: '#f44336', bgcolor: '#ffebee' },
      purple: { color: '#9c27b0', bgcolor: '#f3e5f5' },
      grey: { color: '#9e9e9e', bgcolor: '#f5f5f5' }
    };
    return colorMap[colorName] || colorMap.blue;
  };

  const Icon = getIconComponent(event.icon);
  const colorConfig = getColorConfig(event.color);

  const statusChipConfig = {
    'New Request': { color: '#4caf50', bgcolor: '#e8f5e9' },
    'Approved': { color: '#4caf50', bgcolor: '#e8f5e9' },
    'To Be Approved': { color: '#1976d2', bgcolor: '#e3f2fd' },
    'In Progress': { color: '#ff9800', bgcolor: '#fff3e0' },
    'Pending': { color: '#9e9e9e', bgcolor: '#f5f5f5' }
  };

  const chipConfig = event.statusTag 
    ? (statusChipConfig[event.statusTag] || statusChipConfig.Pending)
    : null;

  return (
    <Box sx={{ display: 'flex', gap: 2, position: 'relative', pb: isLast ? 0 : 3 }}>
      {/* Timeline Line */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
        {/* Dot */}
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: colorConfig.color,
            zIndex: 2,
            flexShrink: 0
          }}
        />
        {/* Vertical Line */}
        {!isLast && (
          <Box
            sx={{
              width: 2,
              flex: 1,
              bgcolor: '#e0e0e0',
              position: 'absolute',
              top: 12,
              bottom: -24
            }}
          />
        )}
      </Box>

      {/* Content Card */}
      <Paper
        sx={{
          flex: 1,
          p: 2.5,
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          boxShadow: 'none',
          transition: 'all 0.2s',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            borderColor: colorConfig.color
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: colorConfig.bgcolor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Icon sx={{ fontSize: 18, color: colorConfig.color }} />
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
              {event.title}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {event.time}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: event.statusTag ? 1.5 : 0, fontSize: '0.875rem' }}>
          {event.description}
        </Typography>

        {event.statusTag && chipConfig && (
          <Chip
            label={event.statusTag}
            size="small"
            sx={{
              bgcolor: chipConfig.bgcolor,
              color: chipConfig.color,
              fontWeight: 500,
              fontSize: '0.75rem',
              height: '24px',
              border: 'none'
            }}
          />
        )}
      </Paper>
    </Box>
  );
};

// Date Separator Component
const DateSeparator = ({ date, onDateClick, isActive }) => (
  <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
    <Chip
      icon={<CalendarIcon sx={{ fontSize: 16 }} />}
      label={date}
      onClick={onDateClick}
      sx={{
        fontWeight: 600,
        color: isActive ? '#1976d2' : '#666',
        fontSize: '0.8rem',
        bgcolor: isActive ? '#e3f2fd' : '#f5f5f5',
        px: 2,
        py: 0.5,
        height: '28px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          bgcolor: '#e3f2fd',
          color: '#1976d2'
        },
        '& .MuiChip-icon': {
          color: isActive ? '#1976d2' : '#666'
        }
      }}
    />
  </Box>
);

// Empty State Component
const EmptyTimelineState = () => (
  <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
    <HourglassEmptyIcon sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
    <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
      No Timeline Events
    </Typography>
    <Typography variant="body2">
      Timeline events will appear here as actions are performed
    </Typography>
  </Box>
);

// Main Timeline Tab Component
const TimelineTab = ({ requestId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [visibleCount, setVisibleCount] = useState(20);

  useEffect(() => {
    setVisibleCount(20);
  }, [events]);

  // Format date and time
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format date for grouping
  const formatDateForGrouping = (dateString) => {
    if (!dateString) return 'Unknown Date';
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Check if date is today
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    }
    // Check if date is yesterday
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    // Otherwise return formatted date
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Fetch timeline events on component mount
  useEffect(() => {
    if (requestId) {
      fetchTimelineEvents();
    }
  }, [requestId]);

  // Fetch all timeline events
  const fetchTimelineEvents = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { isSuccess, data } = await apiCall(
        `${config.baseURL}communication/timeline/${requestId}`,
        {},
        {},
        "GET"
      );
      
      if (isSuccess && data) {
        // Transform API data to match component structure
        const transformedEvents = Array.isArray(data) 
          ? data.map(event => ({
              id: event.id,
              requestId: event.requestId,
              eventType: event.eventType,
              title: event.title,
              description: event.description,
              icon: event.icon,
              color: event.color,
              statusTag: event.statusTag,
              time: formatDateTime(event.eventTimestamp),
              date: formatDateForGrouping(event.eventTimestamp),
              rawDate: event.eventTimestamp,
              emailRecordId: event.emailRecordId,
              userId: event.userId,
              metadata: event.metadata,
              createdAt: event.createdAt
            }))
          : [];
        
        // Sort events by timestamp (most recent first)
        transformedEvents.sort((a, b) => 
          new Date(b.rawDate) - new Date(a.rawDate)
        );
        
        setEvents(transformedEvents);
      } else {
        setError('Failed to load timeline events');
      }
    } catch (err) {
      console.error('Error fetching timeline:', err);
      setError('An error occurred while fetching timeline events');
    } finally {
      setLoading(false);
    }
  };

  // Fetch timeline events by specific date
  const fetchTimelineEventsByDate = async (date) => {
    setLoading(true);
    setError(null);
    
    try {
      const { isSuccess, data } = await apiCall(
        `${config.baseURL}communication/timeline/${requestId}`,
        {},
        { date }, // Send date as query parameter
        "GET"
      );
      
      if (isSuccess && data) {
        const transformedEvents = Array.isArray(data) 
          ? data.map(event => ({
              id: event.id,
              requestId: event.requestId,
              eventType: event.eventType,
              title: event.title,
              description: event.description,
              icon: event.icon,
              color: event.color,
              statusTag: event.statusTag,
              time: formatDateTime(event.eventTimestamp),
              date: formatDateForGrouping(event.eventTimestamp),
              rawDate: event.eventTimestamp,
              emailRecordId: event.emailRecordId,
              userId: event.userId,
              metadata: event.metadata,
              createdAt: event.createdAt
            }))
          : [];
        
        transformedEvents.sort((a, b) => 
          new Date(b.rawDate) - new Date(a.rawDate)
        );
        
        setEvents(transformedEvents);
      } else {
        setError('Failed to load timeline events for selected date');
      }
    } catch (err) {
      setError(`Failed to fetch timeline for selected date: ${err.message}`);
      console.error('Error fetching timeline by date:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle calendar icon click
  const handleCalendarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle popover close
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Handle date change
  const handleDateChange = (event) => {
    const date = event.target.value;
    setSelectedDate(date);
    if (date) {
      fetchTimelineEventsByDate(date);
      handleClose();
    }
  };

  // Clear date filter
  const handleClearFilter = () => {
    setSelectedDate('');
    fetchTimelineEvents();
  };

  const visibleEvents = events.slice(0, visibleCount);
  // Group events by date
  const groupedEvents = visibleEvents.reduce((groups, event) => {
    const date = event.date;
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(event);
    return groups;
  }, {});

  useEffect(() => {
    // if (isMobile) return;

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const fullHeight = document.documentElement.scrollHeight;

      if (
        scrollTop + windowHeight >= fullHeight - 100 &&
        visibleCount < events.length
      ) {
        setVisibleCount((prev) => prev + 20);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [visibleCount, events.length, isMobile]);

  const open = Boolean(anchorEl);

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                Site Inspection Timeline
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Track all activities and status changes for this request
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              {selectedDate && (
                <Chip
                  label={`Filtered: ${new Date(selectedDate).toLocaleDateString()}`}
                  onDelete={handleClearFilter}
                  color="primary"
                  size="small"
                  deleteIcon={<CloseIcon />}
                />
              )}
              <IconButton
                onClick={handleCalendarClick}
                size="small"
                sx={{
                  bgcolor: open ? '#e3f2fd' : '#f5f5f5',
                  '&:hover': { bgcolor: '#e3f2fd' }
                }}
              >
                <CalendarIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Date Picker Popover */}
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Box sx={{ p: 2, minWidth: 250 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Select Date
            </Typography>
            <TextField
              type="date"
              fullWidth
              size="small"
              value={selectedDate}
              onChange={handleDateChange}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>
        </Popover>

        {/* Error State */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={fetchTimelineEvents}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : events.length > 0 ? (
          <Box>
            {Object.entries(groupedEvents).map(([date, dateEvents], groupIndex) => (
              <Box key={date}>
                <DateSeparator 
                  date={date} 
                  onDateClick={handleCalendarClick}
                  isActive={selectedDate !== ''}
                />
                <Box sx={{ pl: 1 }}>
                  {dateEvents.map((event, index) => (
                    <TimelineItem
                      key={event.id}
                      event={event}
                      isFirst={index === 0}
                      isLast={index === dateEvents.length - 1 && groupIndex === Object.keys(groupedEvents).length - 1}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <EmptyTimelineState />
        )}

        {visibleCount < events.length && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default TimelineTab;